import React, { useEffect, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import axiosInstance from '../../../api/axiosInstance';
import PaymentMethodModal from '../../../billing/PaymentMethodModal';
import logger from '../../../../utils/logger';

const DEPARTMENTS = [
  { value: 'sales', label: 'Sales — find and win customers' },
  { value: 'support', label: 'Support — answer and resolve' },
  { value: 'hr', label: 'HR — hiring and people ops' },
  { value: 'finance', label: 'Finance — books and invoices' },
  { value: 'operations', label: 'Operations — keep things moving' },
  { value: 'marketing', label: 'Marketing — content and campaigns' },
];

const StepHire = ({ status, wizardState, showNotification, markProgress, onDone, onBack, reloadStatus }) => {
  const existing = status.agents?.[0];
  const [form, setForm] = useState({ agentName: '', department: 'sales', roleKey: '', description: '' });
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const mode = wizardState?.workspaceMode || status.workspaceMode || (status.hasVerifiedDomain ? 'domain' : 'instant');
  const quote = status.agentBilling;
  const domains = (status.customDomains || []).filter(d => d.verified).map(d => d.name.toLowerCase());
  const eligibleUsers = users.filter(u => u.status === 'active' && !u.isAIAgent && domains.includes(String(u.email || '').split('@')[1]?.toLowerCase()));
  const localPart = String(form.agentName || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '.').replace(/^\.|\.$/g, '').slice(0, 40).replace(/\.$/, '') || 'agent';

  const [creating, setCreating] = useState(false);
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [hired, setHired] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get('/api/ai/role-catalog');
        setRoles(res.data?.data || res.data?.roles || []);
      } catch (error) {
        logger.error('Role catalog load failed:', error);
      }
    })();
  }, []);

  useEffect(() => {
    if (mode !== 'domain') return;
    axiosInstance.get('/api/business/users').then(res => setUsers(res.data?.users || []))
      .catch(() => showNotification?.('Could not load mailboxes. Return to the previous step and retry.', 'error'));
  }, [mode, showNotification]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value, ...(k === 'department' ? { roleKey: '' } : {}) }));
  const deptRoles = roles.filter((r) => r.department === form.department);
  const valid = form.agentName.trim().length >= 2 && quote && confirmed && (mode === 'instant' ? status.platformMail?.enabled : eligibleUsers.some(u => String(u._id) === userId));

  const hire = async () => {
    if (!valid || creating) return;
    setCreating(true);
    try {
      const res = await axiosInstance.post('/api/ai/agents', {
        agentName: form.agentName.trim(),
        department: form.department,
        roleKey: form.roleKey || undefined,
        description: form.description.trim() || undefined,
        ...(mode === 'instant' ? { usePlatformDomain: true } : { userId }),
      });
      const agent = res.data?.data;
      const billing = res.data?.billing;
      setHired({ name: agent?.agentName || form.agentName, email: agent?.emailConfig?.address || '' });
      if (billing?.charged) {
        showNotification?.(`Card charged $${billing.amount} for the first billing period`, 'success');
      }
      await markProgress('agent');
      await reloadStatus();
    } catch (error) {
      const data = error.response?.data;
      if (error.response?.status === 402 && data?.code === 'CARD_REQUIRED') {
        setCardModalOpen(true);
      } else if (error.response?.status === 402 && data?.code === 'CHARGE_FAILED') {
        showNotification?.(data.message || 'Your card was declined — try another card', 'error');
        setCardModalOpen(true);
      } else {
        logger.error('Hire failed:', error);
        showNotification?.(data?.message || 'Could not hire the agent', 'error');
      }
    } finally {
      setCreating(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500';

  // A hire already exists (resume / pre-wizard hire): confirm and move on.
  if (existing && !hired) {
    return (
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">You already have an agent</h2>
        <AgentCard name={existing.name} email={existing.email} department={existing.department} />
        <div className="flex justify-between mt-6">
          {onBack ? <button onClick={onBack} className="px-4 py-2 text-sm text-gray-500">Back</button> : <span />}
          <button
            onClick={async () => { if (!await markProgress('agent')) return; onDone({ agentName: existing.name, agentEmail: existing.email }); }}
            className="px-5 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium"
          >
            Continue with {existing.name}
          </button>
        </div>
      </div>
    );
  }

  if (hired) {
    return (
      <div className="text-center py-4">
        <div className="w-12 h-12 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-3">
          <CheckIcon className="w-7 h-7 text-green-600 dark:text-green-400" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">{hired.name} is hired</h2>
        {hired.email && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Reach them any time at <span className="font-mono text-gray-700 dark:text-gray-300">{hired.email}</span>
          </p>
        )}
        <button
          onClick={() => onDone({ agentName: hired.name, agentEmail: hired.email })}
          className="px-5 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium"
        >
          Continue
        </button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Hire your AI employee</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Choose their role and email identity, then review the cost before hiring.
      </p>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
          <input className={inputCls} value={form.agentName} onChange={set('agentName')} placeholder="e.g. David Gale" />
          <p className="text-xs text-gray-400 mt-1">Pick a real-sounding name — customers will talk to them.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Department</label>
            <select className={inputCls} value={form.department} onChange={set('department')}>
              {DEPARTMENTS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role</label>
            <select className={inputCls} value={form.roleKey} onChange={set('roleKey')} disabled={!deptRoles.length}>
              <option value="">{deptRoles.length ? 'Select a role…' : 'Default'}</option>
              {deptRoles.map((r) => <option key={r.roleKey} value={r.roleKey}>{r.title}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Anything else they should know?</label>
          <textarea className={`${inputCls} min-h-[70px]`} value={form.description} onChange={set('description')}
            placeholder="Optional — e.g. focus on the Ontario market first." />
        </div>
      </div>

      <div className="mt-5 space-y-3 text-sm text-gray-700 dark:text-gray-300">
        {mode === 'instant' ? (
          <p className="break-all">Address preview: <strong>{localPart}@{status.platformMail?.suggestedDomain}</strong><br /><span className="text-xs">The final address may include a number if the name is taken.</span></p>
        ) : (
          <div>
            <label htmlFor="agent-mailbox" className="block mb-1 font-medium">Agent mailbox</label>
            <select id="agent-mailbox" className={inputCls} value={userId} onChange={e => setUserId(e.target.value)}>
              <option value="">Select an available mailbox</option>
              {eligibleUsers.map(u => <option key={u._id} value={String(u._id)}>{u.email}</option>)}
            </select>
            {!eligibleUsers.length && <p className="mt-2">Create an active mailbox on your verified domain in Users, then return here.</p>}
          </div>
        )}
        {quote ? <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <p>AI employee: US${quote.monthly} / month. Due when you hire: US${quote.dueNow}.</p>
          <label className="flex items-start gap-2 mt-3">
            <input type="checkbox" className="mt-1" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
            <span>I have reviewed the price and want to hire this AI employee.</span>
          </label>
        </div> : <p role="alert">Pricing could not be loaded. Refresh before hiring.</p>}
      </div>

      <div className="flex justify-between mt-6">
        {onBack ? <button onClick={onBack} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">Back</button> : <span />}
        <button
          onClick={hire}
          disabled={!valid || creating}
          className="px-5 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-medium"
        >
          {creating ? 'Hiring…' : 'Hire agent'}
        </button>
      </div>

      <PaymentMethodModal
        isOpen={cardModalOpen}
        onClose={() => setCardModalOpen(false)}
        showNotification={showNotification}
        onSuccess={() => {
          setCardModalOpen(false);
          setConfirmed(false);
          reloadStatus();
          showNotification?.('Card saved. Review the price and confirm the hire when ready.', 'success');
        }}
      />
    </div>
  );
};

const AgentCard = ({ name, email, department }) => (
  <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 flex items-center gap-3">
    <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-400 font-semibold">
      {String(name || '?').charAt(0).toUpperCase()}
    </div>
    <div>
      <div className="text-sm font-medium text-gray-900 dark:text-white">{name}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400">{department} · {email}</div>
    </div>
  </div>
);

export default StepHire;
